import { useEffect, useState } from 'react';
import { Autocomplete, Box, Modal, Stack, TextField } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { ProductIdentityValues } from 'services/lookupsService';
import { RawProductIdentity, RawProductIdentityPatch } from 'services/rawProductService';

export type IdentityFieldName = keyof RawProductIdentity;

// What each field is called on screen, what the reviewer needs told about it,
// and which of the server's lists it picks from. Kept in one place because the
// card, the modal's heading and the patch it sends all have to agree on it.
export const IDENTITY_FIELDS: Record<
  IdentityFieldName,
  {
    label: string;
    hint: string;
    // The list a correction is picked from, for every field the graphs choose
    // off a closed list. Absent means free text, which is only the tag.
    options?: keyof ProductIdentityValues;
    multiple: boolean;
    rtl?: boolean;
  }
> = {
  // Free text, because there is no list to pick from: the tag is a sentence
  // written about the pack, not a value read off it. A reviewer edits it when
  // the search was run against the wrong words - which is the one correction
  // that changes which leaves were ever considered.
  tagEn: {
    label: 'Tag (EN)',
    hint: 'What the thing is and the claim the pack leads with, in a handful of words. This is what the leaf search is run against.',
    multiple: false,
  },
  tagAr: {
    label: 'Tag (AR)',
    hint: 'The same two things in Arabic. It is the half that goes wrong: a stage of life or a form rendered as a looser word than the English one.',
    multiple: false,
    rtl: true,
  },
  kind: {
    label: 'Kind',
    hint: 'What the thing is. Leave it empty to say the pack never names one.',
    options: 'kinds',
    multiple: false,
  },
  targetAreas: {
    label: 'Target areas',
    hint: 'The first one decides the category - a leaf names a single place. The rest are kept because the pack named them.',
    options: 'targetAreas',
    multiple: true,
  },
  targetAudience: {
    label: 'Target audience',
    hint: 'Who the product is for, separately from where it acts.',
    options: 'targetAudiences',
    multiple: false,
  },
  targetGender: {
    label: 'Gender',
    hint: 'Who the product is sold to. Leave it empty for a pack that sells to anybody - the Arabic then addresses its reader in the masculine.',
    options: 'targetGenders',
    multiple: false,
  },
  form: {
    label: 'Form',
    hint: 'What the product is made of, not what it comes in. Leave it empty to say the pack states no form.',
    options: 'forms',
    multiple: false,
  },
  packShape: {
    label: 'Pack shape',
    hint: 'What the product comes in and how it leaves the pack - a different fact from the form, not a rival answer to it.',
    options: 'packShapes',
    multiple: false,
  },
};

interface Props {
  // Null when nothing is being edited on this card. One field at a time: each
  // is a separate reading off the pack, and the reviewer corrects the one they
  // can see is wrong rather than being asked to restate all four.
  field: IdentityFieldName | null;
  productName: string;
  // What the row holds now. The modal opens on this every time rather than on
  // whatever was left in it last: an abandoned edit that reappeared on the next
  // open would look like a stored value.
  identity: RawProductIdentity;
  // The closed lists the identity graphs choose from. A correction outside them
  // is a value no later pass can read, so the picker offers nothing else.
  identityValues?: ProductIdentityValues;
  isSaving: boolean;
  onClose: () => void;
  onSave: (patch: RawProductIdentityPatch) => void;
}

export default function IdentityFieldModal({
  field,
  productName,
  identity,
  identityValues,
  isSaving,
  onClose,
  onSave,
}: Props) {
  const [value, setValue] = useState<string | null>(null);
  const [values, setValues] = useState<string[]>([]);

  const config = field ? IDENTITY_FIELDS[field] : null;
  const stored = field ? identity[field] : null;
  const storedList = Array.isArray(stored) ? stored : [];
  const storedSingle = Array.isArray(stored) ? null : stored;
  const storedKey = storedList.join('|');

  useEffect(() => {
    if (!field) return;
    setValue(storedSingle);
    setValues(storedList);
    // storedKey stands in for the array, which is a new object on every fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, storedSingle, storedKey]);

  const changed = config?.multiple ? values.join('|') !== storedKey : value !== storedSingle;

  return (
    <Modal open={Boolean(field)} onClose={isSaving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 440,
          maxWidth: '90vw',
        }}
      >
        <CustomText text={config ? `Edit ${config.label.toLowerCase()}` : ''} fontWeight={700} fontSize={18} />
        <Box mt={0.5}>
          <CustomText text={productName} fontSize={13} color={colors.gray} />
        </Box>

        <Box mt={2}>
          {config && !config.options ? (
            // A plain box rather than a picker: there is no list of tags to
            // offer, and an empty box is a real answer - it says this row has no
            // tag, which is what a row the category pass never reached looks
            // like.
            <TextField
              fullWidth
              size="small"
              multiline
              value={value ?? ''}
              disabled={isSaving}
              onChange={(event) => setValue(event.target.value || null)}
              placeholder="—"
              autoFocus
              inputProps={config.rtl ? { dir: 'rtl' } : undefined}
            />
          ) : config?.multiple ? (
            <Autocomplete
              multiple
              size="small"
              options={config.options ? identityValues?.[config.options] ?? [] : []}
              value={values}
              disabled={isSaving}
              onChange={(_event, next) => setValues(next)}
              renderInput={(params) => (
                <TextField {...params} placeholder={values.length ? '' : '—'} autoFocus />
              )}
            />
          ) : (
            <Autocomplete
              size="small"
              options={config?.options ? identityValues?.[config.options] ?? [] : []}
              value={value}
              disabled={isSaving}
              onChange={(_event, next) => setValue(next)}
              renderInput={(params) => <TextField {...params} placeholder="—" autoFocus />}
            />
          )}
        </Box>

        {config && (
          <Box mt={1}>
            <CustomText text={config.hint} fontSize={11} color={colors.lightgray} />
          </Box>
        )}

        {/* Said here rather than beside the value: it is written to the product
            itself, which is what every later pass reads, so the correction
            outlives this review - and the verdict on the card was reached before
            it. */}
        <Box mt={1}>
          <CustomText
            text="Saved to the product, not to this review. The category verdict was decided against the old value."
            fontSize={11}
            color={colors.lightgray}
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            height={40}
            disabled={isSaving}
            onClick={onClose}
          />
          <CustomButton
            text="Save"
            height={40}
            isLoading={isSaving}
            disabled={!changed}
            onClick={() => {
              if (!field || !config) return;
              onSave(config.multiple ? { [field]: values } : { [field]: value });
            }}
          />
        </Stack>
      </Box>
    </Modal>
  );
}
